import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SignedImage from "@/components/media/SignedImage";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { useMatches } from "@/lib/discovery/useMatches";
import { useConversations } from "@/lib/messaging/useConversations";
import { Star, X, MessageCircle, Heart } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import EndorseDialog from "@/components/endorsements/EndorseDialog";
import { ListRowSkeleton } from "@/components/common/Skeletons";
import EmptyState from "@/components/common/EmptyState";
import type { AppId } from "@/lib/apps";

interface EndorseTarget {
  matchId: string;
  receiverId: string;
  receiverName: string;
  app: AppId | null;
}

const MatchesInner = () => {
  const { matches, loading, archive } = useMatches();
  const { openFromMatch } = useConversations();
  const navigate = useNavigate();
  const [endorseTarget, setEndorseTarget] = useState<EndorseTarget | null>(null);

  const handleMessage = async (matchId: string) => {
    const { id, error } = await openFromMatch(matchId);
    if (id && !error) navigate(`/messages/${id}`);
  };

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <header className="mb-6 flex items-end justify-between gap-4">
        <h1 className="text-3xl font-semibold">Matches</h1>
        <Link
          to="/discover"
          className="rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-accent transition"
        >
          Back to Discover
        </Link>
      </header>

      {loading ? (
        <ListRowSkeleton rows={4} />
      ) : matches.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="No matches yet"
          description="Start swiping in Discover — when someone signals back, they'll show up here."
          ctaLabel="Open Discover"
          ctaHref="/discover"
        />
      ) : (
        <ul className="space-y-3">
          {matches.map((m) => {
            const cover = m.other?.photos?.[0];
            const expiresIn = formatDistanceToNow(new Date(m.expires_at), {
              addSuffix: true,
            });
            return (
              <li
                key={m.id}
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-card"
              >
                <div className="h-14 w-14 flex-none overflow-hidden rounded-xl bg-muted">
                  {cover && (
                    <SignedImage
                      src={cover}
                      alt={m.other?.display_name ?? "Match"}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">
                      {m.other?.display_name ?? "Member"}
                    </p>
                    {m.super_signal && (
                      <Star className="h-4 w-4 text-primary" />
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Expires {expiresIn} · {m.app}
                  </p>
                </div>
                {m.other?.profile_id && (
                  <Link
                    to={`/u/${m.other.profile_id}`}
                    className="rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
                  >
                    View
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => handleMessage(m.id)}
                  className="inline-flex items-center gap-1 rounded-full bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90 transition"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> Message
                </button>
                {m.other?.id && (
                  <button
                    type="button"
                    onClick={() =>
                      setEndorseTarget({
                        matchId: m.id,
                        receiverId: m.other!.id,
                        receiverName: m.other!.display_name ?? "Member",
                        app: (m.app as AppId) ?? null,
                      })
                    }
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
                    aria-label="Endorse this match"
                  >
                    <Heart className="h-3.5 w-3.5" /> Endorse
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Archive match"
                  onClick={() => archive(m.id)}
                  className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground transition"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {endorseTarget && (
        <EndorseDialog
          open={!!endorseTarget}
          onOpenChange={(open) => !open && setEndorseTarget(null)}
          matchId={endorseTarget.matchId}
          receiverId={endorseTarget.receiverId}
          receiverName={endorseTarget.receiverName}
          app={endorseTarget.app}
        />
      )}
    </main>
  );
};

const Matches = () => (
  <OnboardingGate>
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <MatchesInner />
      <SiteFooter />
    </div>
  </OnboardingGate>
);

export default Matches;
